import { useEffect, useMemo, useState } from 'react';

type Params = {
  communityId?: string;
  currentVerified?: number;
  allowedVerified?: number;
  pendingMembers?: number;
  isConfigPage?: boolean;
  // Optional direct percent value when available from backend (e.g., memberLimitConsumed)
  percentConsumed?: number;
};

type UseGrowthMilestoneReturn = {
  shouldShow: boolean;
  closeAndDismiss: () => void;
  percentNotice: {
    shouldShow: boolean;
    title?: string;
    message?: string;
  };
  dismissPercentNotice: () => void;
};

export function useGrowthMilestone({
  communityId,
  currentVerified,
  allowedVerified,
  pendingMembers,
  isConfigPage,
  percentConsumed,
}: Params): UseGrowthMilestoneReturn {
  const [dismissed, setDismissed] = useState(false);
  const [dismissedPercent, setDismissedPercent] = useState(false);

  useEffect(() => {
    setDismissed(false);
    setDismissedPercent(false);
  }, [communityId]);

  const exceeded = useMemo(() => {
    const current = currentVerified ?? 0;
    const allowed = allowedVerified ?? 0;
    return current !== -1 && current >= allowed;
  }, [currentVerified, allowedVerified]);

  // Show on any page if exceeded; config page already passes isConfigPage=true
  const shouldShow = exceeded && !dismissed;

  // Compute percent consumed either from provided percentConsumed or from counts
  const computedPercent = useMemo(() => {
    if (typeof percentConsumed === 'number') return percentConsumed;
    const current = currentVerified ?? 0;
    const allowed = allowedVerified ?? 0;
    if (allowed <= 0) return -1;
    return Math.floor((current / allowed) * 100);
  }, [percentConsumed, currentVerified, allowedVerified]);

  // Percent-based notice when nearing capacity (75%/90%) but not exceeded
  const percentNotice = useMemo(() => {
    if (dismissedPercent)
      return { shouldShow: false, title: undefined, message: undefined };
    if (shouldShow)
      return { shouldShow: false, title: undefined, message: undefined };
    const percent = computedPercent;
    if (percent < 0)
      return { shouldShow: false, title: undefined, message: undefined };

    if (percent >= 90) {
      return {
        shouldShow: true,
        title: `${percent}% - Almost Full ⚡️`,
        message: 'Upgrade now to keep verification smooth as you scale.',
      };
    }
    if (percent >= 75) {
      return {
        shouldShow: true,
        title: `${percent}% and Rising 📈`,
        message:
          'Your community is active, engaged, and filling this tier fast. Upgrade to keep trusted members coming in.',
      };
    }
    return { shouldShow: false, title: undefined, message: undefined };
  }, [computedPercent, shouldShow, dismissedPercent]);

  const dismissPercentNotice = () => setDismissedPercent(true);
  const closeAndDismiss = () => {
    setDismissed(true);
  };

  return { shouldShow, closeAndDismiss, percentNotice, dismissPercentNotice };
}

export default useGrowthMilestone;
